import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface WorkspaceDomain {
  id: string;
  slugId: string;
  label: string;
  coreOntologyId: string;
  coreOntologyName: string;
}

export interface WorkspaceDescriptor {
  id: string;
  slugId: string;
  name: string;
  domain: WorkspaceDomain;
  mode?: string;
  // DevPlan 0125: nur noch Backend-Aktivierungsflag (None|Qdrant).
  // model/dimensions kommen aus den Plattform-Settings.
  embeddingBackend?: string;
}

interface WorkspaceState {
  workspaces: WorkspaceDescriptor[];
  activeWorkspace: WorkspaceDescriptor | null;
  domains: WorkspaceDomain[];
  loading: boolean;
  error: string | null;
  loadWorkspaces: (token?: string | null) => Promise<void>;
  loadDomains: (token?: string | null) => Promise<void>;
  setActiveWorkspace: (id: string, token?: string | null) => Promise<void>;
  createWorkspace: (name: string, domainId: string, token?: string | null, mode?: string) => Promise<WorkspaceDescriptor>;
  createDomain: (
    label: string,
    coreOntologyName: string,
    token?: string | null
  ) => Promise<WorkspaceDomain>;
  updateWorkspace: (
    id: string,
    // DevPlan 0125: model/dimensions kommen aus Platform-Settings, NICHT
    // mehr per Workspace-PUT. `embeddingBackend` bleibt als per-Workspace-
    // Aktivierungsflag (None|Qdrant) erlaubt — er steuert OB der Workspace
    // Vektor-Suche nutzt, nicht WELCHES Modell.
    updates: { name?: string; embeddingBackend?: string },
    token?: string | null
  ) => Promise<WorkspaceDescriptor>;
  clearActiveWorkspace: (token?: string | null) => Promise<void>;
  deleteWorkspace: (id: string, token?: string | null) => Promise<void>;
  deleteDomain: (id: string, token?: string | null) => Promise<void>;
}

export const useWorkspaceStore = create<WorkspaceState>()(
  persist(
    (set, get) => ({
      workspaces: [],
      activeWorkspace: null,
      domains: [],
      loading: false,
      error: null,
      loadWorkspaces: async (token) => {
        if (get().loading) return;          // prevent concurrent loads
        set({ loading: true, error: null });
        try {
          const headers: HeadersInit = {};
          if (token) {
            headers.Authorization = `Bearer ${token}`;
          }

          const [catalogResponse, activeResponse] = await Promise.all([
            fetch('/api/dotnet/workspaces', { headers }),
            fetch('/api/dotnet/workspaces/active', { headers }),
          ]);

          if (!catalogResponse.ok) {
            if (catalogResponse.status === 401 || catalogResponse.status === 403) {
              throw new Error('Unauthorized.');
            }
            throw new Error('Failed to load workspaces.');
          }

          const catalog = (await catalogResponse.json()) as WorkspaceDescriptor[];
          const active = activeResponse.ok
            ? (await activeResponse.json()) as WorkspaceDescriptor | null
            : null;

          const safeCatalog = Array.isArray(catalog)
            ? catalog.filter((entry) => Boolean(entry && entry.id && entry.name))
            : [];

          const safeActive = active && typeof active === 'object' && active.id
            ? active
            : null;

          set({
            workspaces: safeCatalog,
            activeWorkspace: safeActive,
            loading: false,
          });
        } catch (error) {
          set({
            loading: false,
            workspaces: [],
            activeWorkspace: null,
            error: error instanceof Error ? error.message : 'Failed to load workspaces.',
          });
        }
      },
      setActiveWorkspace: async (id, token) => {
        const headers: HeadersInit = {
          'Content-Type': 'application/json',
        };
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const response = await fetch('/api/dotnet/workspaces/active', {
          method: 'PUT',
          headers,
          body: JSON.stringify({ id }),
        });

        if (!response.ok) {
          throw new Error('Failed to set active workspace.');
        }

        const active = (await response.json()) as WorkspaceDescriptor;
        const current = get().workspaces;
        set({
          activeWorkspace: active,
          workspaces: current,
          error: null,
        });
      },
      loadDomains: async (token) => {
        try {
          const headers: HeadersInit = {};
          if (token) {
            headers.Authorization = `Bearer ${token}`;
          }

          const response = await fetch('/api/dotnet/workspaces/domains', { headers });
          if (!response.ok) {
            if (response.status === 401 || response.status === 403) {
              throw new Error('Unauthorized.');
            }
            throw new Error('Failed to load domains.');
          }

          const domains = (await response.json()) as WorkspaceDomain[];
          set({ domains: Array.isArray(domains) ? domains : [] });
        } catch (error) {
          set({
            domains: [],
            error: error instanceof Error ? error.message : 'Failed to load domains.',
          });
        }
      },
      createWorkspace: async (name, domainId, token, mode) => {
        const headers: HeadersInit = {
          'Content-Type': 'application/json',
        };
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const response = await fetch('/api/dotnet/workspaces', {
          method: 'POST',
          headers,
          body: JSON.stringify({ name, domainId, ...(mode ? { mode } : {}) }),
        });

        if (!response.ok) {
          throw new Error('Failed to create workspace.');
        }

        const created = (await response.json()) as WorkspaceDescriptor;
        const current = get().workspaces;
        set({
          workspaces: [...current, created],
          activeWorkspace: created,
          error: null,
        });
        return created;
      },
      createDomain: async (label, coreOntologyName, token) => {
        const headers: HeadersInit = {
          'Content-Type': 'application/json',
        };
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const response = await fetch('/api/dotnet/workspaces/domains', {
          method: 'POST',
          headers,
          body: JSON.stringify({ label, coreOntologyName }),
        });

        if (!response.ok) {
          throw new Error('Failed to create domain.');
        }

        const created = (await response.json()) as WorkspaceDomain;
        const current = get().domains;
        set({ domains: [...current, created], error: null });
        return created;
      },
      updateWorkspace: async (id, updates, token) => {
        const headers: HeadersInit = {
          'Content-Type': 'application/json',
        };
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const response = await fetch(`/api/dotnet/workspaces/${id}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(updates),
        });

        if (!response.ok) {
          throw new Error('Failed to update workspace.');
        }

        const updated = (await response.json()) as WorkspaceDescriptor;
        const current = get().workspaces;
        const currentActive = get().activeWorkspace;
        set({ 
          workspaces: current.map(w => w.id === id ? updated : w),
          activeWorkspace: currentActive?.id === id ? updated : currentActive,
          error: null 
        });
        return updated;
      },
      clearActiveWorkspace: async (token) => {
        const headers: HeadersInit = {};
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const response = await fetch('/api/dotnet/workspaces/active', {
          method: 'DELETE',
          headers,
        });

        if (!response.ok) {
          throw new Error('Failed to clear active workspace.');
        }

        set({ activeWorkspace: null, error: null });
      },
      deleteWorkspace: async (id, token) => {
        const headers: HeadersInit = {};
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const response = await fetch(`/api/dotnet/workspaces/${id}`, {
          method: 'DELETE',
          headers,
        });

        if (!response.ok) {
          if (response.status === 403) {
            throw new Error('Keine Berechtigung zum Löschen dieses Workspaces.');
          }
          throw new Error('Workspace konnte nicht gelöscht werden.');
        }

        // Remove workspace from list
        const current = get().workspaces.filter((w) => w.id !== id);
        const currentActive = get().activeWorkspace;
        
        // Clear active workspace if it was the deleted one
        const newActive = currentActive?.id === id ? null : currentActive;
        
        set({ 
          workspaces: current, 
          activeWorkspace: newActive,
          error: null 
        });
      },
      deleteDomain: async (id, token) => {
        const headers: HeadersInit = {};
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const response = await fetch(`/api/dotnet/workspaces/domains/${id}`, {
          method: 'DELETE',
          headers,
        });

        if (!response.ok) {
          if (response.status === 403) {
            throw new Error('Keine Berechtigung zum Löschen dieser Domain.');
          }
          if (response.status === 409) {
            throw new Error('Domain wird noch von Workspaces verwendet.');
          }
          throw new Error('Domain konnte nicht gelöscht werden.');
        }

        // Remove domain from list
        const current = get().domains.filter((d) => d.id !== id);
        set({ 
          domains: current,
          error: null 
        });
      },
    }),
    {
      name: 'sopho-workspace-store',
      partialize: (state) => ({
        activeWorkspace: state.activeWorkspace,
      }),
    }
  )
);
